import React, { useState, useEffect, useRef } from "react";
import { getImage, GatsbyImage } from "gatsby-plugin-image";
import { motion } from "framer-motion";

import { Swiper, SwiperSlide } from "swiper/react";
import {
  Autoplay,
  Pagination,
  Navigation,
  FreeMode,
  Thumbs,
} from "swiper/modules";
import "swiper/css";
import "swiper/css/free-mode";
import "swiper/css/navigation";
import "swiper/css/thumbs";

const MinimizeButton = ({ onClick, isExpanded }) => {
  return (
    <motion.button
      onClick={onClick}
      className={`border p-.05 lg:p-1 w-6 lg:w-8 ${
        !isExpanded ? "bg-black text-white" : "bg-transparent"
      } hover:bg-black hover:text-white rounded-full`}
      whileHover={{ scale: 1.1 }} // Scale effect on hover
      whileTap={{ scale: 0.9 }} // Scale effect on tap
    >
      {isExpanded ? (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={1}
          stroke="currentColor"
          className="w-full h-full"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14" />
        </svg>
      ) : (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={1.5}
          stroke="currentColor"
          className="w-full h-full"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 4.5v15m7.5-7.5h-15"
          />
        </svg>
      )}
    </motion.button>
  );
};

const ProjectThumbSwiper = ({
  title,
  gallery,
  video,
  setThumbsSwiper,
  isExpanded,
}) => {
  return (
    <motion.div
      id="projectThumbSwiper"
      className="flex w-full h-full overflow-hidden"
      animate={{
        opacity: isExpanded ? 1 : 0,
      }}
      transition={{ duration: 0.3 }}
    >
      <Swiper
        onSwiper={setThumbsSwiper}
        slidesPerView={5}
        spaceBetween={0}
        freeMode={true}
        watchSlidesProgress={false}
        modules={[Autoplay, Pagination, Navigation, FreeMode]}
        grabCursor={true}
        className="c-thumb-swiper w-full h-full"
      >
        {video && (
          <SwiperSlide>
            <iframe
              title={title}
              src={`https://player.vimeo.com/video/${video}?muted=1&autoplay=0&loop=1&autopause=0&background=1:`}
              className="aspect-square w-full pointer-events-none"
            ></iframe>
          </SwiperSlide>
        )}
        {gallery !== null &&
          gallery.map((image, index) => {
            return (
              <SwiperSlide key={index} className="cursor-pointer">
                <GatsbyImage
                  image={getImage(image.localFile)}
                  alt={image.alternativeText || "PPT4U Image"}
                  objectFit="contain"
                  imgClassName="h-full w-full"
                  className="h-full w-full"
                  loading="eager"
                />
              </SwiperSlide>
            );
          })}
      </Swiper>
    </motion.div>
  );
};

const ProjectSwiper = ({ title, gallery, video, thumbsSwiper }) => {
  return (
    <div id="projectSwiper" className="w-full h-full">
      <Swiper
        style={{
          "--swiper-navigation-color": "#000",
          "--swiper-pagination-color": "#fff",
        }}
        loop={gallery !== null ? true : false}
        spaceBetween={0}
        navigation={{
          nextEl: ".swiper-nav-button-next",
          prevEl: ".swiper-nav-button-prev",
        }}
        thumbs={{ swiper: thumbsSwiper }}
        modules={[Autoplay, Pagination, Navigation, FreeMode, Thumbs]}
        className="projectSwiper"
      >
        {video && (
          <SwiperSlide>
            <iframe
              title={title}
              src={`https://player.vimeo.com/video/${video}?muted=1&autoplay=1&loop=1&autopause=0&background=1:`}
              allow="autoplay;"
              className="aspect-video w-full h-full"
            ></iframe>
          </SwiperSlide>
        )}
        {gallery !== null &&
          gallery.map((image, index) => {
            return (
              <SwiperSlide key={index}>
                <GatsbyImage
                  image={getImage(image.localFile)}
                  alt={image.alternativeText || "PPT4U Image"}
                  style={{
                    maxWidth: 1920,
                    maxHeight: "100%",
                    margin: "0 auto",
                  }}
                  className="w-full h-full"
                  objectFit="contain"
                />
              </SwiperSlide>
            );
          })}
        <button className="swiper-nav-button-prev group">
          <span className="border border-l-0 border-black p-.05 lg:p-1 bg-black text-white group-hover:bg-white group-hover:text-black group-hover:border-black w-10 h-10">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1}
              stroke="currentColor"
              className="w-full h-full"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15.75 19.5 8.25 12l7.5-7.5"
              />
            </svg>
          </span>
        </button>
        <button className="swiper-nav-button-next group">
          <span className="border border-r-0 border-black p-.05 lg:p-1 bg-black text-white group-hover:bg-white group-hover:text-black group-hover:border-black w-10 h-10">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1}
              stroke="currentColor"
              className="w-full h-full"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="m8.25 4.5 7.5 7.5-7.5 7.5"
              />
            </svg>
          </span>
        </button>
      </Swiper>
    </div>
  );
};

const ProjectGallery = ({ project }) => {
  const [thumbsSwiper, setThumbsSwiper] = useState();
  const [isExpanded, setIsExpanded] = useState(false);
  const { title, gallery, video } = project.strapiProject;

  const toggleExpansion = () => {
    setIsExpanded(!isExpanded);
  };

  const mainVariants = {
    open: { height: "70vh", transition: { duration: 0.5, ease: "easeInOut" } },
    closed: {
      height: "90vh",
      transition: { duration: 0.5, ease: "easeInOut" },
    },
  };

  const secondVariants = {
    open: {
      height: "30vh",
      transition: { delay: 0.2, duration: 0.5, ease: "easeInOut" },
    },
    closed: {
      height: "10vh",
      transition: { delay: 0.2, duration: 0.5, ease: "easeInOut" },
    },
  };

  return (
    <>
      <motion.div
        className={`relative overflow-hidden`}
        animate={isExpanded ? "open" : "closed"}
        variants={mainVariants}
      >
        <ProjectSwiper
          title={title}
          gallery={gallery}
          video={video}
          thumbsSwiper={thumbsSwiper}
        />
      </motion.div>
      <motion.div
        id="projectInfo"
        className={`w-full bottom-0 overflow-hidden z-10 pb-[env(safe-area-inset-bottom)]`}
        animate={isExpanded ? "open" : "closed"}
        variants={secondVariants}
      >
        <div className={`relative h-full w-full flex flex-col`}>
          <motion.div
            className={`flex px-4 lg:px-[2vw] py-4 w-full overflow-hidden`}
            style={{ minHeight: isExpanded ? "auto" : "100%" }}
          >
            <div className={`flex items-center gap-x-4 w-full h-full`}>
              <MinimizeButton
                onClick={toggleExpansion}
                isExpanded={isExpanded}
              />
              <h2 className="uppercase items-center leading-none text-[clamp(1.44rem,1.2304rem+1.048vw,2.488rem)]">
                {title}
              </h2>
            </div>
          </motion.div>

          {gallery !== null && (
            <ProjectThumbSwiper
              title={title}
              gallery={gallery}
              video={video}
              setThumbsSwiper={setThumbsSwiper}
              isExpanded={isExpanded}
            />
          )}
        </div>
      </motion.div>
    </>
  );
};

export default ProjectGallery;
